// Admin action on an assessment diet: type in a result the Institute already
// has on paper — a candidate assessed before logbook assessment went digital,
// or one otherwise settled off the pipeline. Mirrors CreateLogbookModal in
// adminLogbookActions.tsx (same member typeahead), but records straight onto
// an assessment diet's result set rather than creating a plain logbook.
import { useEffect, useState } from "react";
import { FileEdit, X, Loader2, Search } from "lucide-react";
import { useLazyAdminMemberLookupQuery } from "../api/adminLogbookApi";
import { useRecordManualAssessmentResultMutation } from "../api/logbookAssessmentDietApi";

const STAGE_OPTIONS = [1, 2, 3];

const RESULT_OPTIONS: { value: "pass" | "resit_6_months" | "resit_12_months"; label: string }[] = [
  { value: "pass", label: "Successful" },
  { value: "resit_6_months", label: "To re-present — 6 months" },
  { value: "resit_12_months", label: "To resubmit — 12 months" },
];

function errMsg(e: any, fallback: string): string {
  return e?.data?.message || e?.error || fallback;
}

export function RecordManualResultButton({ dietId, dietTitle }: { dietId: string; dietTitle?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm font-semibold hover:bg-amber-100"
      >
        <FileEdit className="w-4 h-4" /> Record Manual Result
      </button>
      {open && <ManualResultModal dietId={dietId} dietTitle={dietTitle} onClose={() => setOpen(false)} />}
    </>
  );
}

function ManualResultModal({
  dietId,
  dietTitle,
  onClose,
}: {
  dietId: string;
  dietTitle?: string;
  onClose: () => void;
}) {
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [picked, setPicked] = useState<any | null>(null);
  const [stage, setStage] = useState(1);
  const [result, setResult] = useState<"pass" | "resit_6_months" | "resit_12_months">("pass");
  const [totalScore, setTotalScore] = useState("");
  const [comment, setComment] = useState("");
  const [manualSource, setManualSource] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const [triggerLookup, { data: results = [], isFetching }] = useLazyAdminMemberLookupQuery();
  const [recordResult, { isLoading: saving }] = useRecordManualAssessmentResultMutation();

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (debouncedQ.length >= 2) triggerLookup(debouncedQ);
  }, [debouncedQ, triggerLookup]);

  const submit = async () => {
    setErr(null);
    setOk(null);
    if (!picked) {
      setErr("Select a member first.");
      return;
    }
    if (totalScore !== "") {
      const n = Number(totalScore);
      if (Number.isNaN(n) || n < 0 || n > 100) {
        setErr("Total score must be a number between 0 and 100.");
        return;
      }
    }
    try {
      await recordResult({
        id: dietId,
        user_id: picked.user_id,
        stage,
        result,
        total_score: totalScore !== "" ? Number(totalScore) : undefined,
        comment: comment.trim() || undefined,
        manual_source: manualSource.trim() || undefined,
      }).unwrap();
      setOk(`Manual result recorded and published for ${picked.name} — they can see it now.`);
      setPicked(null);
      setQ("");
      setDebouncedQ("");
      setTotalScore("");
      setComment("");
      setManualSource("");
      setResult("pass");
      setStage(1);
    } catch (e) {
      setErr(errMsg(e, "Could not record the manual result."));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h2 className="text-lg font-bold text-gray-800">Record Manual Result</h2>
            {dietTitle && <p className="text-xs text-gray-500 mt-0.5">{dietTitle}</p>}
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
            For a result the Institute already has on paper — e.g. assessed before logbook assessment
            went digital. This is recorded as a <b>manual entry</b>, clearly marked as such wherever it
            appears, and is <b>published immediately</b> — the member can see it as soon as you save.
          </div>

          {/* Member picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Member</label>
            {picked ? (
              <div className="flex items-center justify-between border rounded-lg px-3 py-2 bg-gray-50">
                <div>
                  <div className="text-sm font-medium text-gray-900">{picked.name}</div>
                  <div className="text-xs text-gray-500">{picked.membership_no || picked.email}</div>
                </div>
                <button onClick={() => setPicked(null)} className="text-xs text-indigo-600 hover:underline">
                  Change
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                <input
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by name, email or membership no…"
                  className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-100"
                />
                {debouncedQ.length >= 2 && (
                  <div className="mt-1 border rounded-lg max-h-52 overflow-y-auto">
                    {isFetching ? (
                      <div className="px-3 py-2 text-sm text-gray-500 flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" /> Searching…
                      </div>
                    ) : results.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-500">No members found.</div>
                    ) : (
                      results.map((m: any) => (
                        <button
                          key={m.user_id}
                          onClick={() => {
                            setPicked(m);
                            setOk(null);
                            setErr(null);
                          }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 border-b last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">{m.name}</div>
                          <div className="text-xs text-gray-500">{m.membership_no || m.email}</div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Stage + result */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stage</label>
              <select
                value={stage}
                onChange={(e) => setStage(Number(e.target.value))}
                className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gray-100"
              >
                {STAGE_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    Stage {s}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Result</label>
              <select
                value={result}
                onChange={(e) => setResult(e.target.value as typeof result)}
                className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gray-100"
              >
                {RESULT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Total score (optional)</label>
              <input
                type="number"
                min={0}
                max={100}
                value={totalScore}
                onChange={(e) => setTotalScore(e.target.value)}
                placeholder="0 – 100"
                className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Source (optional)</label>
              <input
                value={manualSource}
                onChange={(e) => setManualSource(e.target.value)}
                placeholder="e.g. Feb 2022 result sheet"
                className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gray-100"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Comment (optional)</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              placeholder="Any note on the record…"
              className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gray-100 resize-y"
            />
          </div>

          {err && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{err}</div>}
          {ok && <div className="text-sm text-green-700 bg-green-50 border border-green-100 rounded-lg px-3 py-2">{ok}</div>}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50">
            Close
          </button>
          <button
            onClick={submit}
            disabled={saving || !picked}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-600 text-white text-sm hover:bg-amber-700 disabled:opacity-50"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Record Result
          </button>
        </div>
      </div>
    </div>
  );
}
